import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { problemasPerguntaTreinamento, numeroVideoTreinamento, MAX_ALTERNATIVAS_TREINAMENTO, MIN_ALTERNATIVAS_TREINAMENTO } from '@fiodecobre/shared';
import { Modal } from '../../components/Modal';
import { useDialog } from '../../components/DialogProvider';
import { api, ApiError, getToken } from '../../lib/api';

// GERENCIAR VIDEOS DE TREINAMENTO (so admin — decisao do Ruan, 30/09/2026, migration 0304).
// Ele escolheu a tela de cadastro em vez de publicar por deploy: "da mais trabalho mas fica mais
// organizado". Aqui se cria o video, envia o .mp4, escreve o questionario e decide quando a
// equipe ve.
//
// Regras da casa aplicadas:
// - Video novo nasce OCULTO e pergunta nova nasce SEM a certa marcada: deixar pronto seria
//   decidir por ele.
// - Escolher o arquivo JA envia (com progresso e cancelar): arquivo escolhido que so vale depois
//   de um segundo botao e' o campo que se esquece de mandar.
// - Salvar apagado diz por que; sair com alteracao nao salva pergunta antes.
// - Nada se apaga: retirar pergunta/video so' esconde (tentativas antigas apontam pra eles).

type PerguntaAdmin = { id: number; pergunta: string; alternativas: string[]; certa: number };
type VideoAdmin = {
  id: number; slug: string; titulo: string; resumo: string; arquivo: string | null; duracao: string | null;
  ordem: number; ativo: boolean; secao_id: number | null; tentativas: number; perguntas: PerguntaAdmin[];
};
type SecaoAdmin = { id: number; nome: string; ordem: number; videos: number };
// Grupo da lista da esquerda: as secoes na ordem, e por ultimo os videos ainda sem secao.
type Grupo = { secao: SecaoAdmin | null; numero: number | null; videos: VideoAdmin[] };
type Rascunho = { chave: string; id?: number; pergunta: string; alternativas: string[]; certa: number | null };

const CAMPO = 'no-upper w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const SELO_VISIVEL = { background: '#fde68a', color: '#000' };

let seq = 0;
const novaChave = () => `n${++seq}`;
const paraRascunho = (p: PerguntaAdmin): Rascunho => ({ chave: `p${p.id}`, id: p.id, pergunta: p.pergunta, alternativas: [...p.alternativas], certa: p.certa });
const perguntaNova = (): Rascunho => ({ chave: novaChave(), pergunta: '', alternativas: ['', '', '', ''], certa: null });

function mmss(seg: number): string {
  const s = Math.round(seg);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function lerDuracao(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    const fim = (r: string | null) => { URL.revokeObjectURL(url); resolve(r); };
    v.onloadedmetadata = () => fim(Number.isFinite(v.duration) ? mmss(v.duration) : null);
    v.onerror = () => fim(null);
    v.src = url;
  });
}

function statusVideo(v: VideoAdmin): { texto: string; visivel: boolean } {
  if (!v.arquivo) return { texto: 'Sem arquivo', visivel: false };
  return v.ativo ? { texto: 'Visível', visivel: true } : { texto: 'Oculto', visivel: false };
}

export function ModalGerenciarVideos({ onFechar }: { onFechar: () => void }) {
  const dialog = useDialog();
  const [videos, setVideos] = useState<VideoAdmin[] | null>(null);
  const [secoes, setSecoes] = useState<SecaoAdmin[]>([]);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [selId, setSelId] = useState<number | null>(null);
  // Secao sendo renomeada (id) ou criada ('nova'), com o texto digitado.
  const [editSecao, setEditSecao] = useState<{ id: number | 'nova'; nome: string } | null>(null);

  // Rascunho do video selecionado.
  const [titulo, setTitulo] = useState('');
  const [resumo, setResumo] = useState('');
  const [ativo, setAtivo] = useState(false);
  const [secaoId, setSecaoId] = useState<number | null>(null);
  const [perguntas, setPerguntas] = useState<Rascunho[]>([]);
  const [salvando, setSalvando] = useState<'dados' | 'perguntas' | null>(null);
  const [msg, setMsg] = useState<{ onde: 'dados' | 'perguntas' | 'arquivo'; texto: string } | null>(null);

  // Envio do .mp4
  const [envio, setEnvio] = useState<{ nome: string; pct: number; mb: number } | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);

  const sel = videos?.find((v) => v.id === selId) ?? null;

  // Lista da esquerda: secoes na ordem (numeradas 1, 2...) e os videos de cada uma pela ordem
  // DENTRO dela; no fim, "Sem seção" com os que ainda nao tem (video novo nasce assim).
  const grupos = useMemo<Grupo[]>(() => {
    const vs = videos ?? [];
    const porOrdem = (a: VideoAdmin, b: VideoAdmin) => a.ordem - b.ordem || a.id - b.id;
    const g: Grupo[] = secoes.map((s, i) => ({ secao: s, numero: i + 1, videos: vs.filter((v) => v.secao_id === s.id).sort(porOrdem) }));
    const soltos = vs.filter((v) => v.secao_id == null || !secoes.some((s) => s.id === v.secao_id)).sort(porOrdem);
    if (soltos.length) g.push({ secao: null, numero: null, videos: soltos });
    return g;
  }, [videos, secoes]);

  const carregar = useCallback(async (manterSel?: number | null) => {
    try {
      const [lista, listaSecoes] = await Promise.all([
        api.get<VideoAdmin[]>('/api/treinamento/admin/videos'),
        api.get<SecaoAdmin[]>('/api/treinamento/admin/secoes'),
      ]);
      setVideos(lista);
      setSecoes(listaSecoes);
      setErroLista(null);
      const alvo = manterSel !== undefined ? manterSel : null;
      const escolhido = lista.find((v) => v.id === alvo) ?? lista[0] ?? null;
      setSelId(escolhido?.id ?? null);
      return lista;
    } catch (e) {
      setErroLista(e instanceof ApiError ? e.message : 'Não foi possível carregar os vídeos.');
      return null;
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  // Troca de video (ou recarga): o rascunho volta ao que esta gravado. A mensagem ("Salvo.") so'
  // some na TROCA de video — na recarga depois de salvar, ela e' justamente o que tem que ficar.
  const ultimoSelRef = useRef<number | null>(null);
  useEffect(() => {
    if (!sel) return;
    setTitulo(sel.titulo);
    setResumo(sel.resumo);
    setAtivo(sel.ativo);
    setSecaoId(sel.secao_id);
    setPerguntas(sel.perguntas.map(paraRascunho));
    if (ultimoSelRef.current !== sel.id) setMsg(null);
    ultimoSelRef.current = sel.id;
  }, [sel]);

  const dadosAlterados = !!sel && (titulo !== sel.titulo || resumo !== sel.resumo || ativo !== sel.ativo || secaoId !== sel.secao_id);
  // O que falta pra poder marcar "Visivel" — o servidor recusa as mesmas duas coisas.
  const faltaParaVisivel = [
    ...(sel && !sel.arquivo ? ['envie o arquivo do vídeo'] : []),
    ...(secaoId == null ? ['escolha a seção'] : []),
  ];
  const perguntasAlteradas = useMemo(() => {
    if (!sel) return false;
    if (perguntas.length !== sel.perguntas.length) return true;
    return perguntas.some((p, i) => {
      const o = sel.perguntas[i]!;
      return p.id !== o.id || p.pergunta !== o.pergunta || p.certa !== o.certa
        || p.alternativas.length !== o.alternativas.length || p.alternativas.some((a, k) => a !== o.alternativas[k]);
    });
  }, [perguntas, sel]);
  const temAlteracao = dadosAlterados || perguntasAlteradas;

  const problemasPerguntas = perguntas.flatMap((p, i) => problemasPerguntaTreinamento(p).map((t) => `Pergunta ${i + 1}: ${t}`));

  async function podeSair(): Promise<boolean> {
    if (envio) {
      await dialog.alerta('Espere o envio do vídeo terminar, ou cancele o envio.', { titulo: 'Enviando vídeo' });
      return false;
    }
    if (!temAlteracao) return true;
    return dialog.confirmar('Há alterações não salvas neste vídeo. Sair sem salvar?', {
      titulo: 'Alterações não salvas', textoConfirmar: 'Sair sem salvar', textoCancelar: 'Continuar editando',
    });
  }

  async function selecionar(id: number) {
    if (id === selId) return;
    if (!(await podeSair())) return;
    setSelId(id);
  }

  async function fechar() {
    if (await podeSair()) onFechar();
  }

  async function novoVideo() {
    if (!(await podeSair())) return;
    try {
      const criado = await api.post<{ id: number }>('/api/treinamento/admin/videos', { titulo: 'Novo vídeo', resumo: '' });
      await carregar(criado.id);
    } catch (e) {
      await dialog.alerta(e instanceof ApiError ? e.message : 'Não foi possível criar o vídeo.', { tipo: 'erro' });
    }
  }

  // Recarrega mantendo o rascunho do video aberto: so' ordem/secoes mudaram, nao o que ele digita.
  async function recarregarSemPerderRascunho() {
    const [lista, listaSecoes] = await Promise.all([
      api.get<VideoAdmin[]>('/api/treinamento/admin/videos'),
      api.get<SecaoAdmin[]>('/api/treinamento/admin/secoes'),
    ]);
    setSecoes(listaSecoes);
    setVideos((antes) => lista.map((v) => {
      const a = antes?.find((x) => x.id === v.id);
      return a && a.id === selId ? { ...a, ordem: v.ordem } : v;
    }));
  }

  /** Sobe/desce um video DENTRO do grupo (secao) dele. */
  async function mover(grupo: Grupo, id: number, delta: -1 | 1) {
    const ids = grupo.videos.map((v) => v.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    try {
      await api.put('/api/treinamento/admin/ordem', { ids });
      await recarregarSemPerderRascunho();
    } catch (e) {
      await dialog.alerta(e instanceof ApiError ? e.message : 'Não foi possível mudar a ordem.', { tipo: 'erro' });
    }
  }

  async function moverSecao(id: number, delta: -1 | 1) {
    const ids = secoes.map((s) => s.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    try {
      await api.put('/api/treinamento/admin/secoes/ordem', { ids });
      await recarregarSemPerderRascunho();
    } catch (e) {
      await dialog.alerta(e instanceof ApiError ? e.message : 'Não foi possível mudar a ordem.', { tipo: 'erro' });
    }
  }

  async function gravarSecao() {
    if (!editSecao || !editSecao.nome.trim()) return;
    try {
      if (editSecao.id === 'nova') await api.post('/api/treinamento/admin/secoes', { nome: editSecao.nome });
      else await api.put(`/api/treinamento/admin/secoes/${editSecao.id}`, { nome: editSecao.nome });
      setEditSecao(null);
      await recarregarSemPerderRascunho();
    } catch (e) {
      await dialog.alerta(e instanceof ApiError ? e.message : 'Não foi possível salvar a seção.', { tipo: 'erro' });
    }
  }

  async function retirarSecao(s: SecaoAdmin) {
    const ok = await dialog.confirmar(`Retirar a seção "${s.nome}"? Ela está vazia e some da lista.`, {
      titulo: 'Retirar seção', textoConfirmar: 'Retirar', textoCancelar: 'Cancelar',
    });
    if (!ok) return;
    try {
      await api.post(`/api/treinamento/admin/secoes/${s.id}/retirar`, {});
      await recarregarSemPerderRascunho();
    } catch (e) {
      await dialog.alerta(e instanceof ApiError ? e.message : 'Não foi possível retirar a seção.', { tipo: 'erro' });
    }
  }

  async function salvarDados() {
    if (!sel) return;
    setSalvando('dados');
    setMsg(null);
    try {
      await api.put(`/api/treinamento/admin/videos/${sel.id}`, { titulo, resumo, ativo, secao_id: secaoId });
      await carregar(sel.id);
      setMsg({ onde: 'dados', texto: 'Salvo.' });
    } catch (e) {
      setMsg({ onde: 'dados', texto: e instanceof ApiError ? e.message : 'Não foi possível salvar.' });
    } finally {
      setSalvando(null);
    }
  }

  async function salvarPerguntas() {
    if (!sel || problemasPerguntas.length > 0) return;
    setSalvando('perguntas');
    setMsg(null);
    try {
      await api.put(`/api/treinamento/admin/videos/${sel.id}/perguntas`, {
        perguntas: perguntas.map((p) => ({ id: p.id, pergunta: p.pergunta, alternativas: p.alternativas, certa: p.certa })),
      });
      await carregar(sel.id);
      setMsg({ onde: 'perguntas', texto: 'Perguntas salvas.' });
    } catch (e) {
      setMsg({ onde: 'perguntas', texto: e instanceof ApiError ? e.message : 'Não foi possível salvar as perguntas.' });
    } finally {
      setSalvando(null);
    }
  }

  async function escolherArquivo(file: File) {
    if (!sel) return;
    if (file.type !== 'video/mp4') {
      setMsg({ onde: 'arquivo', texto: 'Escolha um arquivo .mp4.' });
      return;
    }
    if (sel.arquivo) {
      const ok = await dialog.confirmar(`Trocar o vídeo atual deste item por "${file.name}"?`, {
        titulo: 'Trocar o vídeo', textoConfirmar: 'Trocar o vídeo', textoCancelar: 'Cancelar',
      });
      if (!ok) return;
    }
    const duracao = await lerDuracao(file);
    const fd = new FormData();
    // `duracao` ANTES do arquivo: o multer so' entrega ao servidor os campos que chegam antes dele.
    if (duracao) fd.append('duracao', duracao);
    fd.append('video', file);
    const mb = file.size / (1024 * 1024);
    setMsg(null);
    setEnvio({ nome: file.name, pct: 0, mb });
    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.open('POST', `/api/treinamento/admin/videos/${sel.id}/arquivo`);
    const token = getToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) setEnvio((e) => (e ? { ...e, pct: Math.round((ev.loaded / ev.total) * 100) } : e));
    };
    const idAlvo = sel.id;
    xhr.onload = async () => {
      xhrRef.current = null;
      setEnvio(null);
      let corpo: { erro?: string } = {};
      try { corpo = JSON.parse(xhr.responseText); } catch { /* resposta nao-JSON (ex.: 413 do nginx) */ }
      if (xhr.status >= 200 && xhr.status < 300) {
        await carregar(idAlvo);
        setMsg({ onde: 'arquivo', texto: `Vídeo enviado${duracao ? ` (${duracao})` : ''}.` });
      } else {
        const texto = xhr.status === 413 ? 'O servidor recusou: arquivo grande demais.' : (corpo.erro ?? `Falhou (erro ${xhr.status}).`);
        setMsg({ onde: 'arquivo', texto });
      }
    };
    xhr.onerror = () => { xhrRef.current = null; setEnvio(null); setMsg({ onde: 'arquivo', texto: 'A conexão caiu durante o envio. Tente de novo.' }); };
    xhr.onabort = () => { xhrRef.current = null; setEnvio(null); setMsg({ onde: 'arquivo', texto: 'Envio cancelado. Nada foi trocado.' }); };
    xhr.send(fd);
  }

  // --- edicao das perguntas ---
  const mudarPergunta = (i: number, f: (p: Rascunho) => Rascunho) => setPerguntas((ps) => ps.map((p, k) => (k === i ? f(p) : p)));
  const moverPergunta = (i: number, delta: -1 | 1) => setPerguntas((ps) => {
    const j = i + delta;
    if (j < 0 || j >= ps.length) return ps;
    const n = [...ps];
    [n[i], n[j]] = [n[j]!, n[i]!];
    return n;
  });
  const tirarAlternativa = (i: number, k: number) => mudarPergunta(i, (p) => ({
    ...p,
    alternativas: p.alternativas.filter((_, x) => x !== k),
    // A certa acompanha a lista: apagar a propria certa desmarca (nunca escolhe outra sozinha).
    certa: p.certa === null ? null : p.certa === k ? null : p.certa > k ? p.certa - 1 : p.certa,
  }));

  const rodape = (
    <button type="button" onClick={() => void fechar()} className="btn-secundario px-4 py-2 rounded-md">Fechar</button>
  );

  return (
    <Modal aberto onFechar={() => void fechar()} titulo="Gerenciar vídeos de treinamento" telaCheia rodape={rodape}>
      <div className="flex gap-6 min-h-full" data-gerenciar-videos>
        {/* ---------- lista ---------- */}
        <aside className="w-80 flex-shrink-0">
          <button type="button" onClick={() => void novoVideo()} className="btn-primario w-full px-3 py-2 rounded-md inline-flex items-center justify-center gap-2">
            <Plus size={16} /> Novo vídeo
          </button>
          {erroLista && <p className="text-sm font-bold mt-3">{erroLista}</p>}
          {videos === null && !erroLista && <p className="text-sm text-gray-500 mt-3">Carregando...</p>}
          <div className="mt-3 space-y-4" data-grupos-secoes>
            {grupos.map((g, gi) => (
              <div key={g.secao?.id ?? 'sem-secao'}>
                {/* Cabecalho da secao: nome, ordem, renomear, retirar (so vazia). */}
                {g.secao && editSecao?.id === g.secao.id ? (
                  <div className="flex items-center gap-1 mb-1">
                    <input type="text" autoFocus value={editSecao.nome} maxLength={80}
                      onChange={(e) => setEditSecao({ ...editSecao, nome: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') void gravarSecao(); if (e.key === 'Escape') setEditSecao(null); }}
                      className={CAMPO} />
                    <button type="button" onClick={() => void gravarSecao()} disabled={!editSecao.nome.trim()} className="btn-primario px-2 py-1.5 rounded-md text-sm disabled:opacity-50">Salvar</button>
                    <button type="button" onClick={() => setEditSecao(null)} className="btn-secundario px-2 py-1.5 rounded-md text-sm">Cancelar</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 mb-1">
                    <span className="flex-1 font-bold text-[14px] text-gray-800 truncate">
                      {g.secao ? `${g.numero}. ${g.secao.nome}` : 'Sem seção'}
                    </span>
                    {g.secao && (
                      <>
                        <button type="button" title="Subir a seção" disabled={gi === 0} onClick={() => void moverSecao(g.secao!.id, -1)} className="p-1 disabled:opacity-30"><ArrowUp size={14} /></button>
                        <button type="button" title="Descer a seção" disabled={gi === secoes.length - 1} onClick={() => void moverSecao(g.secao!.id, 1)} className="p-1 disabled:opacity-30"><ArrowDown size={14} /></button>
                        <button type="button" title="Renomear a seção" onClick={() => setEditSecao({ id: g.secao!.id, nome: g.secao!.nome })} className="p-1"><Pencil size={14} /></button>
                        <button type="button" disabled={g.videos.length > 0}
                          title={g.videos.length > 0 ? 'Só dá pra retirar seção vazia: mude os vídeos de seção antes' : 'Retirar a seção'}
                          onClick={() => void retirarSecao(g.secao!)} className="p-1 disabled:opacity-30"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                )}
                {g.videos.length === 0 && <p className="text-xs text-gray-500 pl-1">Nenhum vídeo nesta seção.</p>}
                <ol className="space-y-1">
                  {g.videos.map((v, i) => {
                    const st = statusVideo(v);
                    const atual = v.id === selId;
                    const numero = g.numero != null ? `${numeroVideoTreinamento(g.numero, i + 1)} ` : '';
                    return (
                      <li key={v.id} className={`flex items-center gap-1 rounded-md border ${atual ? 'border-blue-600' : 'border-gray-200'}`}>
                        <button type="button" onClick={() => void selecionar(v.id)} className="flex-1 text-left px-2 py-2 min-w-0">
                          <div className="text-sm font-medium truncate">{numero}{v.titulo}</div>
                          <div className="mt-1 flex items-center gap-2 text-xs">
                            <span className="px-1.5 py-0.5 rounded font-bold" style={st.visivel ? SELO_VISIVEL : undefined}>{st.texto}</span>
                            <span className="text-gray-500">{v.perguntas.length} pergunta{v.perguntas.length === 1 ? '' : 's'}</span>
                          </div>
                        </button>
                        <div className="flex flex-col">
                          <button type="button" title="Subir" disabled={i === 0} onClick={() => void mover(g, v.id, -1)} className="p-1 disabled:opacity-30"><ArrowUp size={14} /></button>
                          <button type="button" title="Descer" disabled={i === g.videos.length - 1} onClick={() => void mover(g, v.id, 1)} className="p-1 disabled:opacity-30"><ArrowDown size={14} /></button>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
          {editSecao?.id === 'nova' ? (
            <div className="flex items-center gap-1 mt-4">
              <input type="text" autoFocus value={editSecao.nome} maxLength={80} placeholder="Nome da seção..."
                onChange={(e) => setEditSecao({ id: 'nova', nome: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') void gravarSecao(); if (e.key === 'Escape') setEditSecao(null); }}
                className={CAMPO} />
              <button type="button" onClick={() => void gravarSecao()} disabled={!editSecao.nome.trim()} className="btn-primario px-2 py-1.5 rounded-md text-sm disabled:opacity-50">Criar</button>
              <button type="button" onClick={() => setEditSecao(null)} className="btn-secundario px-2 py-1.5 rounded-md text-sm">Cancelar</button>
            </div>
          ) : (
            <button type="button" onClick={() => setEditSecao({ id: 'nova', nome: '' })}
              className="btn-secundario w-full mt-4 px-3 py-1.5 rounded-md inline-flex items-center justify-center gap-2 text-sm">
              <Plus size={14} /> Nova seção
            </button>
          )}
        </aside>

        {/* ---------- editor ---------- */}
        {sel && (
          <section className="flex-1 min-w-0 max-w-4xl space-y-8 pb-8">
            {/* Dados */}
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Dados do vídeo</h3>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
              <input type="text" value={titulo} maxLength={150} onChange={(e) => setTitulo(e.target.value)} className={CAMPO} />
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">Seção</label>
              {/* Combo do cadastro de secoes (regra da casa: valor que vem de cadastro nao se digita).
                  O texto de fundo e' instrucao, nunca uma secao valida. */}
              <select value={secaoId ?? ''} onChange={(e) => setSecaoId(e.target.value ? Number(e.target.value) : null)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Escolha a seção...</option>
                {secoes.map((s, i) => <option key={s.id} value={s.id}>{i + 1}. {s.nome}</option>)}
              </select>
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">Resumo (aparece embaixo do vídeo)</label>
              <textarea value={resumo} rows={3} maxLength={2000} onChange={(e) => setResumo(e.target.value)} className={CAMPO} />
              <label className={`flex items-center gap-2 mt-3 text-sm ${faltaParaVisivel.length === 0 || ativo ? 'cursor-pointer' : 'opacity-60'}`}>
                <input type="checkbox" checked={ativo} disabled={faltaParaVisivel.length > 0 && !ativo} onChange={(e) => setAtivo(e.target.checked)} />
                Visível para a equipe
              </label>
              {faltaParaVisivel.length > 0 && (
                <p className="text-xs text-gray-700 mt-1">Para deixar visível: {faltaParaVisivel.join(' e ')}.</p>
              )}
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => void salvarDados()} disabled={!dadosAlterados || !titulo.trim() || salvando !== null || (ativo && faltaParaVisivel.length > 0)}
                  className="btn-primario px-4 py-2 rounded-md disabled:opacity-50">
                  {salvando === 'dados' ? 'Salvando...' : 'Salvar dados'}
                </button>
                {!titulo.trim() && <span className="text-sm text-gray-700">Escreva o título.</span>}
                {titulo.trim() && !dadosAlterados && msg?.onde !== 'dados' && <span className="text-sm text-gray-500">Nada alterado.</span>}
                {msg?.onde === 'dados' && <span className="text-sm font-bold">{msg.texto}</span>}
              </div>
            </div>

            {/* Arquivo */}
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Arquivo do vídeo</h3>
              {sel.arquivo ? (
                <div className="flex gap-4 items-start flex-wrap">
                  <video key={sel.arquivo} src={sel.arquivo} controls preload="metadata" className="w-80 rounded bg-black" />
                  <div className="text-sm text-gray-700">
                    <div className="break-all">{sel.arquivo.split('/').pop()}</div>
                    {sel.duracao && <div className="mt-1">Duração: {sel.duracao}</div>}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Nenhum arquivo enviado ainda.</p>
              )}
              <input ref={inputArquivo} type="file" accept="video/mp4" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void escolherArquivo(f); }} />
              {envio ? (
                <div className="mt-3 max-w-md">
                  <div className="text-sm">Enviando {envio.nome} ({envio.mb.toFixed(1)} MB) — {envio.pct}%</div>
                  <div className="h-2 mt-1 rounded bg-gray-200 overflow-hidden">
                    <div className="h-full bg-blue-600" style={{ width: `${envio.pct}%` }} />
                  </div>
                  <button type="button" onClick={() => xhrRef.current?.abort()} className="btn-secundario mt-2 px-3 py-1.5 rounded-md inline-flex items-center gap-2">
                    <X size={14} /> Cancelar envio
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button type="button" onClick={() => inputArquivo.current?.click()} className="btn-primario px-4 py-2 rounded-md inline-flex items-center gap-2">
                    <Upload size={16} /> {sel.arquivo ? 'Trocar o vídeo (.mp4)' : 'Enviar o vídeo (.mp4)'}
                  </button>
                  {msg?.onde === 'arquivo' && <span className="text-sm font-bold">{msg.texto}</span>}
                </div>
              )}
            </div>

            {/* Perguntas */}
            <div>
              <h3 className="font-bold text-gray-800">Questionário ({perguntas.length} pergunta{perguntas.length === 1 ? '' : 's'})</h3>
              {sel.tentativas > 0 && (
                <p className="text-sm text-gray-700 mt-1">
                  Já há {sel.tentativas} tentativa{sel.tentativas === 1 ? '' : 's'} gravada{sel.tentativas === 1 ? '' : 's'}. Editar não muda a nota de quem já respondeu.
                </p>
              )}
              <ol className="mt-3 space-y-4">
                {perguntas.map((p, i) => (
                  <li key={p.chave} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-sm flex-1">Pergunta {i + 1}</span>
                      <button type="button" title="Subir" disabled={i === 0} onClick={() => moverPergunta(i, -1)} className="p-1 disabled:opacity-30"><ArrowUp size={14} /></button>
                      <button type="button" title="Descer" disabled={i === perguntas.length - 1} onClick={() => moverPergunta(i, 1)} className="p-1 disabled:opacity-30"><ArrowDown size={14} /></button>
                      <button type="button" title="Retirar pergunta" onClick={() => setPerguntas((ps) => ps.filter((_, k) => k !== i))}
                        className="p-1 inline-flex items-center gap-1 text-sm"><Trash2 size={14} /> Retirar</button>
                    </div>
                    <textarea value={p.pergunta} rows={2} maxLength={1000} placeholder="Escreva a pergunta..."
                      onChange={(e) => mudarPergunta(i, (x) => ({ ...x, pergunta: e.target.value }))} className={CAMPO} />
                    <div className="text-xs text-gray-600 mt-2 mb-1">Alternativas — marque a bolinha da resposta certa</div>
                    <div className="space-y-1">
                      {p.alternativas.map((a, k) => (
                        <div key={k} className="flex items-center gap-2">
                          <input type="radio" name={`certa-${p.chave}`} checked={p.certa === k} title="Esta é a resposta certa"
                            onChange={() => mudarPergunta(i, (x) => ({ ...x, certa: k }))} />
                          <input type="text" value={a} maxLength={500} placeholder={`Alternativa ${k + 1}`}
                            onChange={(e) => mudarPergunta(i, (x) => ({ ...x, alternativas: x.alternativas.map((y, z) => (z === k ? e.target.value : y)) }))}
                            className={CAMPO} />
                          {p.certa === k && <span className="text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap" style={SELO_VISIVEL}>Certa</span>}
                          <button type="button" title="Apagar alternativa" disabled={p.alternativas.length <= MIN_ALTERNATIVAS_TREINAMENTO}
                            onClick={() => tirarAlternativa(i, k)} className="p-1 disabled:opacity-30"><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                    {p.alternativas.length < MAX_ALTERNATIVAS_TREINAMENTO && (
                      <button type="button" onClick={() => mudarPergunta(i, (x) => ({ ...x, alternativas: [...x.alternativas, ''] }))}
                        className="mt-2 text-sm inline-flex items-center gap-1"><Plus size={14} /> Alternativa</button>
                    )}
                  </li>
                ))}
              </ol>
              <button type="button" onClick={() => setPerguntas((ps) => [...ps, perguntaNova()])}
                className="btn-secundario mt-3 px-3 py-1.5 rounded-md inline-flex items-center gap-2">
                <Plus size={16} /> Adicionar pergunta
              </button>
              <div className="mt-4 flex items-start gap-3 flex-wrap">
                <button type="button" onClick={() => void salvarPerguntas()}
                  disabled={!perguntasAlteradas || problemasPerguntas.length > 0 || salvando !== null}
                  className="btn-primario px-4 py-2 rounded-md disabled:opacity-50">
                  {salvando === 'perguntas' ? 'Salvando...' : 'Salvar perguntas'}
                </button>
                <div className="text-sm">
                  {problemasPerguntas.length > 0 && (
                    <ul className="text-gray-800">{problemasPerguntas.slice(0, 6).map((t) => <li key={t}>{t}</li>)}
                      {problemasPerguntas.length > 6 && <li>e mais {problemasPerguntas.length - 6}...</li>}</ul>
                  )}
                  {problemasPerguntas.length === 0 && !perguntasAlteradas && msg?.onde !== 'perguntas' && <span className="text-gray-500">Nada alterado.</span>}
                  {msg?.onde === 'perguntas' && <span className="font-bold">{msg.texto}</span>}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}
